import React, { useState, useEffect, useCallback } from 'react'; 
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { FaHome, FaProjectDiagram, FaTasks, FaChartBar, FaBars, FaSignOutAlt, FaCheck, FaTimes, FaPlus, FaCalendarAlt } from 'react-icons/fa';
import axios from 'axios';
import ManagerProjects from './ManagerProjects';
import CreateTask from './CreateTask';
import MyTasks from './MyTasks';
import TeamPerformance from './TeamPerformance';
import TaskCalendar from './TaskCalendar';
import API_URL from '../config';
import { disconnectWebSocket } from '../websocket';
import NotificationBell from './NotificationBell';
import NotificationDialog from "./NotificationDialog";
import LanguageToggle from './LanguageToggle';
import { useLanguage } from '../context/LanguageContext';
import { getStatusBadgeClass } from '../utils/statusBadge';
import { formatDateTime24 } from '../utils/dateTimeFormat';
import { getLocalDevSubtasks, isLocalDevCompanyId, LOCAL_DEV_PROJECTS, updateLocalDevSubtask } from '../utils/localDevAuth';
import { clearStoredAuth, getApiErrorMessage, getAuthHeaders, getLoginPath, getStoredAccessToken, handleExpiredSession } from '../utils/authSession';
// Enhanced CSS Reset to eliminate global style interference
const styles = `
  html, body, #root, #root > div {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box;
    min-height: 100vh;
  }
  * {
    box-sizing: inherit;
  }
`;
const ManagerView = ({ user, setUser }) => {
  const { t, locale } = useLanguage();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasksDueToday, setTasksDueToday] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const sidebarIdentityInitial = 'M';
  const isLocalDevSession = isLocalDevCompanyId(user?.company?.id) || isLocalDevCompanyId(localStorage.getItem('company_id'));
  const effectiveCompanyId = user?.company?.id || localStorage.getItem('company_id') || '1';

  const navigate = useNavigate();
  
  const fetchData = useCallback(async () => {
    try {
      const token = getStoredAccessToken();
      if (isLocalDevSession) {
        const today = new Date().toISOString().slice(0, 10);
        const localProjects = LOCAL_DEV_PROJECTS.filter(project => project.company.id === user.company?.id);
        const localTasks = getLocalDevSubtasks().filter(task => task.major_task.project.company.id === user.company?.id);
        setProjects(localProjects.filter(p => ['ongoing', 'planned'].includes(p.status)));
        setTasksDueToday(localTasks.filter(task => task.due_date?.startsWith(today)));
        setPendingApprovals(localTasks.filter(task => task.status === 'pending_approval'));
        setMyTasks(localTasks.filter(task => task.assigned_to?.id === user.id));
        setError('');
        return;
      }
      if (!token) {
        setError('Session expired. Please log in again.');
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      const [projectsRes, dueTodayRes, pendingRes, myTasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/tasks-due-today/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/pending-approvals/`, {
          headers: getAuthHeaders(token),
        }),
        axios.get(`${API_URL}/api/subtasks/`, {
          headers: getAuthHeaders(token),
          params: { project_status: 'ongoing,planned' },
        }),
      ]);
      const projectsWithTasks = await Promise.all(projectsRes.data.map(async (project) => {
        if (project.major_tasks && project.major_tasks.length > 0) {
          const majorTasks = await Promise.all(project.major_tasks.map(async (taskId) => {
            try {
              const taskRes = await axios.get(`${API_URL}/api/major-tasks/${taskId}/`, {
                headers: getAuthHeaders(token),
              });
              return taskRes.data;
            } catch (error) {
              console.error(`Error fetching major task ${taskId}:`, error);
              return null;
            }
          }));
          return { ...project, major_tasks: majorTasks.filter(task => task !== null) };
        }
        return { ...project, major_tasks: [] };
      }));
      const tasksDueTodayWithDetails = await Promise.all(dueTodayRes.data.map(async (task) => {
        try {
          const taskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, {
            headers: getAuthHeaders(token),
          });
          return { ...task, major_task: taskRes.data };
        } catch (error) {
          console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
          return task;
        }
      }));
      const pendingApprovalsWithDetails = await Promise.all(pendingRes.data.map(async (task) => {
        try {
          const taskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, {
            headers: getAuthHeaders(token),
          });
          return { ...task, major_task: taskRes.data };
        } catch (error) {
          console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
          return task;
        }
      }));
      const myTasksWithDetails = await Promise.all(myTasksRes.data.map(async (task) => {
        try {
          const taskRes = await axios.get(`${API_URL}/api/major-tasks/${task.major_task_id}/`, {
            headers: getAuthHeaders(token),
          });
          return { ...task, major_task: taskRes.data };
        } catch (error) {
          console.error(`Error fetching major task ${task.major_task_id} for subtask ${task.id}:`, error);
          return task;
        }
      }));
      const filteredProjects = projectsWithTasks.filter(p => ['ongoing', 'planned'].includes(p.status));
      setProjects(filteredProjects);
      setTasksDueToday(tasksDueTodayWithDetails);
      setPendingApprovals(pendingApprovalsWithDetails);
      setMyTasks(myTasksWithDetails.filter(t => t.assigned_to?.id === user.id));
      setError('');
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        setError('Session expired. Please log in again.');
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(getApiErrorMessage(error, {
        fallback: t('toast.failedLoadData'),
      }));
    }
  }, [user, navigate, t, isLocalDevSession, effectiveCompanyId]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(prev => !prev);
  };
  const toggleResponsiveSidebar = () => {
    if (window.matchMedia('(min-width: 1024px)').matches) {
      toggleSidebar();
      return;
    }

    toggleMobileSidebar();
  };
  const handleMobileNavClick = () => {
    setIsMobileSidebarOpen(false);
  };
  const navLinkClass = ({ isActive }) =>
    `shell-nav-link ${isActive ? 'shell-nav-link-active' : ''} ${!isSidebarOpen ? 'lg:justify-center' : ''}`;
  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };
  const handleApproveTask = async (taskId) => {
    try {
      if (isLocalDevSession) {
        updateLocalDevSubtask(taskId, { status: 'completed' });
        setSuccess(t('manager.taskApprovedSuccess'));
        setError('');
        fetchData();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      await axios.patch(`${API_URL}/api/subtasks/${taskId}/`, {
        status: 'completed',
      }, {
        headers: getAuthHeaders(token),
      });
      setSuccess(t('manager.taskApprovedSuccess'));
      setError('');
      fetchData();
    } catch (error) {
      console.error('Error approving task:', error);
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(t('manager.failedApproveTask'));
      setSuccess('');
    }
  };
  const handleDeclineTask = async (taskId) => {
    try {
      if (isLocalDevSession) {
        updateLocalDevSubtask(taskId, { status: 'ongoing' });
        setSuccess(t('manager.taskDeclinedSuccess'));
        setError('');
        fetchData();
        return;
      }
      const token = getStoredAccessToken();
      if (!token) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      await axios.patch(`${API_URL}/api/subtasks/${taskId}/`, {
        status: 'ongoing',
      }, {
        headers: getAuthHeaders(token),
      });
      setSuccess(t('manager.taskDeclinedSuccess'));
      setError('');
      fetchData();
    } catch (error) {
      console.error('Error declining task:', error);
      if (error.response?.status === 401) {
        handleExpiredSession({ navigate, companyId: effectiveCompanyId });
        return;
      }
      setError(t('manager.failedDeclineTask'));
      setSuccess('');
    }
  };


  const handleLogout = () => {
    disconnectWebSocket();
    clearStoredAuth();
    setUser(null);
    navigate(getLoginPath({ companyId: effectiveCompanyId }));
  };
  const Dashboard = () => (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">{t('manager.welcome', { name: user.code || 'Manager' })}</h1>
      {error && <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>}
      {success && <p className="text-green-500 mb-3 sm:mb-4 text-sm sm:text-base">{success}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-4 sm:mb-6">
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => navigate('/manager/projects')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaProjectDiagram className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">{t('manager.ongoingProjects')}</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{projects.length}</p>
          </div>
        </div>
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => toggleSection('tasksDueToday')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaTasks className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">{t('manager.majorTasksToday')}</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{tasksDueToday.length}</p>
          </div>
        </div>
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => toggleSection('pendingApprovals')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaChartBar className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">{t('manager.pendingApprovals')}</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{pendingApprovals.length}</p>
          </div>
        </div>
        <div
          className="card p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 cursor-pointer hover:bg-hover-bg transition"
          onClick={() => toggleSection('myTasks')}
        >
          <div className="bg-primary p-2 sm:p-3 rounded-full">
            <FaTasks className="text-white" size={20} />
          </div>
          <div>
            <h3 className="text-secondary-text font-semibold text-sm sm:text-base">{t('common.myTasks')}</h3>
            <p className="text-xl sm:text-2xl font-bold text-text">{myTasks.length}</p>
          </div>
        </div>
      </div>
      {activeSection === 'tasksDueToday' && (
        <div className="mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">{t('manager.majorTasksToday')}</h2>
          {tasksDueToday.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">{t('manager.noMajorTasksDueToday')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="soft-table-head">
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('common.project')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Major Task</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('manager.subtaskTitle')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Assignee</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Due Date & Time</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksDueToday.map(task => (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.assigned_to?.email || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.due_date ? formatDateTime24(task.due_date, locale) : t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeSection === 'pendingApprovals' && (
        <div className="mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">{t('manager.pendingApprovals')}</h2>
          {pendingApprovals.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">{t('manager.noPendingApprovals')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="soft-table-head">
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('common.project')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Major Task</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('manager.subtaskTitle')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('manager.teamMember')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Due Date & Time</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('manager.action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingApprovals.map(task => (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.assigned_to?.email || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.due_date ? formatDateTime24(task.due_date, locale) : t('common.na')}</td>
                      <td className="p-2 sm:p-3">
                        <button
                          className="primary-btn px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base mr-2"
                          onClick={() => handleApproveTask(task.id)}
                        >
                          <FaCheck className="inline mr-1 sm:mr-2" /> {t('manager.approveTask')}
                        </button>
                        <button
                          className="danger-btn px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base"
                          onClick={() => handleDeclineTask(task.id)}
                        >
                          <FaTimes className="inline mr-1 sm:mr-2" /> {t('manager.declineTask')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {activeSection === 'myTasks' && (
        <div className="mt-4 sm:mt-6">
          <h2 className="text-xl sm:text-2xl font-semibold text-secondary-text mb-3 sm:mb-4">{t('common.myTasks')}</h2>
          {myTasks.length === 0 ? (
            <p className="text-text italic text-sm sm:text-base">{t('manager.noTasksAssigned')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-max w-full text-left border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="soft-table-head">
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('common.project')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Major Task</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">{t('manager.subtaskTitle')}</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Due Date & Time</th>
                    <th className="p-2 sm:p-3 min-w-[120px] sm:min-w-[150px]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myTasks.map(task => (
                    <tr key={task.id} className="border-b border-secondary table-row-hover">
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.project?.name || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.major_task?.name || t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">{task.name}</td>
                      <td className="p-2 sm:p-3 text-text">{task.due_date ? formatDateTime24(task.due_date, locale) : t('common.na')}</td>
                      <td className="p-2 sm:p-3 text-text">
                        <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
  return (
    <>
      <style>{styles}</style>
      <div className="flex min-h-screen bg-background font-sans relative overflow-x-hidden">
        {/* Mobile Overlay */}
        {isMobileSidebarOpen && (
            <div 
                className="overlay-backdrop z-30 lg:hidden"
                onClick={() => setIsMobileSidebarOpen(false)}
            />
        )}

        {/* Sidebar */}
        <div className={`shell-sidebar transition-all duration-300 fixed top-0 left-0 h-full z-40 flex flex-col
            w-64 ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
            lg:translate-x-0 ${isSidebarOpen ? 'lg:w-80' : 'lg:w-20'}`}>
          <div className={`border-b border-border px-3 py-4 sm:px-4 ${!isSidebarOpen ? 'lg:px-3' : ''}`}>
            <div className={`relative flex items-start justify-between gap-3 ${!isSidebarOpen ? 'lg:justify-center' : ''}`}>
              <div className={`flex min-w-0 items-center gap-3 ${!isSidebarOpen ? 'lg:hidden' : ''}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-btn">
                  <span className="text-sm font-semibold sm:text-base">{sidebarIdentityInitial}</span>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-text sm:text-lg">
                    {user.company ? user.company.name : t('common.noCompany')}
                  </p>
                  <p className="truncate text-xs text-secondary-text sm:text-sm">{user.email}</p>
                </div>
              </div>
              <div className={`hidden h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-btn ${!isSidebarOpen ? 'lg:flex' : ''}`}>
                <span className="text-sm font-semibold">{sidebarIdentityInitial}</span>
              </div>
              <div className={`${!isSidebarOpen ? 'lg:absolute lg:right-3 lg:top-0' : ''}`}>
                {/* Desktop Toggle */}
                <button onClick={toggleSidebar} className="hidden rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none lg:block">
                    <FaBars size={16} className="text-base sm:text-lg md:text-xl" />
                </button>
                {/* Mobile Close */}
                <button onClick={toggleMobileSidebar} className="ml-auto rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none lg:hidden">
                    <FaBars size={16} className="text-base sm:text-lg" />
                </button>
              </div>
            </div>
          </div>
          <nav className="flex justify-between items-center mt-8 sm:mt-3 md:mt-4">
            <ul className='flex justify-between items-center flex-col w-full'>
              <li className="w-full">
                <NavLink end to="/manager" onClick={handleMobileNavClick} className={navLinkClass}>
                  <FaHome className={`mr-1 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                  <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.dashboard')}</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink to="/manager/projects" onClick={handleMobileNavClick} className={navLinkClass}>
                  <FaProjectDiagram className={`mr-2 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                  <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.projects')}</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink to="/manager/create-task" onClick={handleMobileNavClick} className={navLinkClass}>
                  <FaPlus className={`mr-2 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                  <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.createTask')}</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink to="/manager/my-tasks" onClick={handleMobileNavClick} className={navLinkClass}>
                  <FaTasks className={`mr-2 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                  <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.myTasks')}</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink to="/manager/calendar" onClick={handleMobileNavClick} className={navLinkClass}>
                  <FaCalendarAlt className={`mr-2 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                  <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.calendar')}</span>
                </NavLink>
              </li>
              <li className="w-full">
                <NavLink to="/manager/team-assignments" onClick={handleMobileNavClick} className={navLinkClass}>
                  <FaChartBar className={`mr-2 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                  <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('manager.teamPerformance')}</span>
                </NavLink>
              </li>
              <li className={`w-full shell-nav-link ${!isSidebarOpen ? 'lg:justify-center' : ''}`} onClick={() => { handleLogout(); handleMobileNavClick(); }}>
                <div className={`flex items-center w-full ${!isSidebarOpen ? 'lg:justify-center' : ''}`}>
                    <FaSignOutAlt className={`mr-2 sm:mr-1 ${!isSidebarOpen ? 'lg:mr-0' : ''}`} size={16} />
                    <span className={`text-sm sm:text-base ${!isSidebarOpen ? 'lg:hidden' : ''}`}>{t('common.logout')}</span>
                </div>
              </li>
            </ul>
          </nav>
        </div>
        <div className={`min-w-0 flex-1 transition-all duration-300 ${isSidebarOpen ? 'lg:ml-80' : 'lg:ml-20'}`}>
          <div className={`shell-topbar fixed top-0 right-0 z-30 flex items-center justify-between p-2 sm:p-3 md:p-4 transition-all duration-300 left-0 ${isSidebarOpen ? 'lg:left-80' : 'lg:left-20'}`}>
            <div className="flex items-center">
                {/* Mobile Toggle */}
                <button
                  type="button"
                  onClick={toggleResponsiveSidebar}
                  className="mr-3 rounded-xl p-2 text-secondary-text transition hover:bg-primary/5 hover:text-primary focus:outline-none"
                  aria-label="Toggle sidebar"
                  aria-expanded={isMobileSidebarOpen || isSidebarOpen}
                >
                    <FaBars size={20} />
                </button>
                <span className="hidden truncate text-text text-lg font-semibold sm:block sm:text-xl">
                    {user.company ? user.company.name : t('common.noCompany')}
                </span>
            </div>
            <div className="ml-3 flex items-center space-x-2 sm:space-x-2 md:space-x-3">
              <div>
                {/* Top right bell */}
                <div className="flex items-center justify-end gap-3">
                  <LanguageToggle />
                  <NotificationBell user={user} onAllNotificationsClick={() => setDialogOpen(true)} />
                </div>

                {/* Manager module main content goes here */}

                {/* Full notifications dialog */}
                  <NotificationDialog
                  open={dialogOpen}
                  onClose={() => setDialogOpen(false)}
                />
              </div>
              <button onClick={handleLogout} className="secondary-btn hidden items-center space-x-1 sm:inline-flex !px-3 !py-2 text-sm sm:text-base">
                <FaSignOutAlt className="text-base sm:text-lg" />
                <span>{t('common.logout')}</span>
              </button>
            </div>
          </div>
          <div className="mt-16 p-4 sm:p-5 md:p-6 overflow-x-auto">
            <div className="max-w-5xl sm:max-w-6xl md:max-w-7xl mx-auto">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="projects" element={<ManagerProjects user={user} />} />
                <Route path="create-task" element={<CreateTask user={user} setIsSidebarOpen={setIsSidebarOpen} />} />
                <Route path="my-tasks" element={<MyTasks user={user} />} />
                <Route path="calendar" element={<TaskCalendar user={user} />} />
                <Route path="team-assignments" element={<TeamPerformance user={user} />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default ManagerView;
